import { useForm } from 'react-hook-form';
import { ESTADOS, ESTADO_LABEL, OPERACION_LABEL, TIPOS, TIPO_LABEL, type Property } from '../lib/types';
import { useSaveProperty } from '../api/properties';

type FormData = Omit<Property, 'property_id' | 'codigo' | 'cover' | 'media' | 'publicada_web'> & {
  publicada_web: boolean;
};

const field =
  'mt-1 w-full rounded-xl border border-deep/15 bg-soft px-3.5 py-2.5 text-base outline-none focus:border-brand transition';
const label = 'block text-xs font-bold uppercase tracking-wider text-ink/50';

export default function PropertyForm({
  initial,
  onDone,
}: {
  initial?: Property;
  onDone: (saved: Property) => void;
}) {
  const save = useSaveProperty();
  const { register, handleSubmit, formState } = useForm<FormData>({
    defaultValues: initial
      ? { ...initial, publicada_web: initial.publicada_web === 'si' }
      : {
          tipo: 'piso',
          estado: 'disponible',
          operacion: 'alquiler',
          destacada: false,
          publicada_web: false,
        },
  });

  const onSubmit = handleSubmit(async (data) => {
    const saved = await save.mutateAsync({
      ...data,
      property_id: initial?.property_id,
      publicada_web: data.publicada_web as unknown as Property['publicada_web'],
    });
    onDone(saved);
  });

  return (
    <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
      <label className="block sm:col-span-2">
        <span className={label}>Dirección *</span>
        <input {...register('direccion', { required: true })} className={field} placeholder="Calle Serrano 21, 3ºA" />
      </label>
      <label className="block">
        <span className={label}>Zona</span>
        <input {...register('zona')} className={field} placeholder="Salamanca" />
      </label>
      <label className="block">
        <span className={label}>Tipo</span>
        <select {...register('tipo')} className={field}>
          {TIPOS.map((t) => <option key={t} value={t}>{TIPO_LABEL[t] ?? t}</option>)}
        </select>
      </label>
      <label className="block">
        <span className={label}>Estado</span>
        <select {...register('estado')} className={field}>
          {ESTADOS.map((e) => <option key={e} value={e}>{ESTADO_LABEL[e]}</option>)}
        </select>
      </label>
      <label className="block">
        <span className={label}>Operación</span>
        <select {...register('operacion')} className={field}>
          {(Object.keys(OPERACION_LABEL) as (keyof typeof OPERACION_LABEL)[]).map((o) => (
            <option key={o} value={o}>{OPERACION_LABEL[o]}</option>
          ))}
        </select>
      </label>
      <div className="grid grid-cols-3 gap-3 sm:col-span-2">
        <label className="block">
          <span className={label}>m²</span>
          <input type="number" {...register('m2', { valueAsNumber: true })} className={field} />
        </label>
        <label className="block">
          <span className={label}>Habitaciones</span>
          <input type="number" {...register('habitaciones', { valueAsNumber: true })} className={field} />
        </label>
        <label className="block">
          <span className={label}>Baños</span>
          <input type="number" {...register('banos', { valueAsNumber: true })} className={field} />
        </label>
      </div>
      <label className="block">
        <span className={label}>Precio alquiler €/mes</span>
        <input type="number" {...register('precio_alquiler', { valueAsNumber: true })} className={field} />
      </label>
      <label className="block">
        <span className={label}>Precio venta €</span>
        <input type="number" {...register('precio_venta', { valueAsNumber: true })} className={field} />
      </label>
      <label className="block sm:col-span-2">
        <span className={label}>Descripción</span>
        <textarea rows={3} {...register('descripcion')} className={`${field} resize-none`} />
      </label>
      <div className="flex gap-6 sm:col-span-2">
        <label className="flex items-center gap-2 text-sm text-ink/70">
          <input type="checkbox" {...register('destacada')} className="h-4 w-4 accent-brand" />
          Destacada
        </label>
        <label className="flex items-center gap-2 text-sm text-ink/70">
          <input type="checkbox" {...register('publicada_web')} className="h-4 w-4 accent-brand" />
          Publicada en la web
        </label>
      </div>
      <div className="sm:col-span-2 flex items-center gap-3">
        <button
          type="submit"
          disabled={formState.isSubmitting}
          className="rounded-full bg-brand px-7 py-2.5 text-sm font-bold text-white transition hover:bg-deep disabled:opacity-50"
        >
          {initial ? 'Guardar cambios' : 'Crear vivienda'}
        </button>
        {save.isError && <span className="text-sm text-red-700">No se pudo guardar. Reintenta.</span>}
      </div>
    </form>
  );
}
